import React, { useCallback, useState } from 'react';

import {
  FaExclamationTriangle,
  FaCheckCircle,
  FaFileDownload,
  FaTimesCircle,
} from 'react-icons/fa';
import { Button } from 'react-bootstrap';

import Modal from '../../../Modal';
import api from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';
import getResponseErrors from '../../../../utils/getResponseErrors';

import { formataCNPJ, formataCPF } from '../../../../utils/funcoes';

import {
  Container,
  WarningBox,
  PatientInfo,
  PatientRow,
  PatientLabel,
  PatientValue,
  ActionButtons,
  DownloadLink,
  StatusMessage,
} from './styles';
import { IPacientePlano } from '..';

interface IContrato {
  ConCodigo: number;
  ConAssinado: boolean;
  ConArquivo?: string;
  ConExternalId?: string;
  arquivo_url?: string;
}

type ModalAssinaturaContratoProps = {
  contrato: IContrato | null;
  paciente: IPacientePlano | null;
  show: boolean;
  onClose: () => void;
  onAssinaturaIniciada: () => void;
};

const ModalAssinaturaContrato: React.FC<ModalAssinaturaContratoProps> = ({
  contrato,
  paciente,
  show,
  onClose,
  onAssinaturaIniciada,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleIniciarAssinatura = useCallback(async () => {
    if (!contrato) return;

    setLoading(true);
    try {
      const recipients = [
        {
          name: paciente?.PacNome,
          email: paciente?.PacEmail.toLocaleLowerCase(),
        },
      ];
      await api.post(
        `planos/contrato/iniciarassinatura/${contrato.ConCodigo}`,
        { recipients },
      );
      addToast({
        title: 'Processo de assinatura iniciado',
        type: 'success',
        description: 'O paciente receberá o link para assinatura digital.',
      });
      onAssinaturaIniciada();
      onClose();
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível iniciar a assinatura',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [
    contrato,
    paciente?.PacNome,
    paciente?.PacEmail,
    addToast,
    onAssinaturaIniciada,
    onClose,
  ]);

  const handleAssinarManual = useCallback(async () => {
    setLoading(true);
    if (!contrato) return;
    const { ConCodigo, ConAssinado } = contrato;
    try {
      await api.patch(`planos/contratoAssinado/${ConCodigo}`, {
        ConAssinado: !ConAssinado,
      });

      onAssinaturaIniciada();
      onClose();
    } catch (error) {
      const mensagem = getResponseErrors(error);
      addToast({
        type: 'error',
        title: 'Não foi possível atualizar informação',
        description: mensagem,
      });
    } finally {
      setLoading(false);
    }
  }, [contrato, onAssinaturaIniciada, onClose, addToast]);

  const getDocumentoFormatado = (): string => {
    if (!paciente) return '';
    return paciente.PacPessoaFisica
      ? formataCPF(paciente.PacCPF)
      : formataCNPJ(paciente.PacCNPJ);
  };

  const getNomeExibicao = (): string => {
    if (!paciente) return '';
    return paciente.PacNomeSocial && paciente.PacNomeSocial.trim() !== ''
      ? paciente.PacNomeSocial
      : paciente.PacNome;
  };

  const renderContent = (): React.ReactNode => {
    if (!contrato || !paciente) return null;

    const jaAssinado = contrato.ConAssinado && contrato.ConArquivo;
    const assinadoPresencialmente =
      contrato.ConAssinado && !contrato.ConExternalId;
    const processoIniciado = contrato.ConExternalId && !contrato.ConAssinado;
    const temPendencia =
      !paciente.PacEmail ||
      (paciente.PacPessoaFisica && !paciente.PacCPF) ||
      (paciente.PacPessoaFisica === false && !paciente?.PacCNPJ);

    if (temPendencia) {
      return (
        <>
          <StatusMessage warning>
            <FaTimesCircle size={24} /> Contrato não pode ser assinado
          </StatusMessage>
          <br />
          <div>
            <b>Pendências para assinatura:</b>
            {!paciente.PacEmail && <p> - E-mail não cadastrado</p>}
            {paciente.PacPessoaFisica && !paciente.PacCPF && (
              <p> - CPF não cadastrado</p>
            )}
            {paciente.PacPessoaFisica === false && !paciente.PacCNPJ && (
              <p> - CNPJ não cadastrado</p>
            )}
          </div>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleAssinarManual}
            disabled={loading}
          >
            Assinatura Física Impressa
          </Button>
        </>
      );
    }

    if (jaAssinado) {
      return (
        <>
          <StatusMessage success>
            <FaCheckCircle size={24} /> Contrato assinado digitalmente
          </StatusMessage>
          <div className="text-center mt-3">
            <DownloadLink
              href={contrato.arquivo_url || ''}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaFileDownload size={20} /> Baixar contrato assinado
            </DownloadLink>
          </div>
        </>
      );
    }

    if (assinadoPresencialmente) {
      return (
        <StatusMessage warning>
          <FaTimesCircle size={24} /> Este contrato foi assinado presencialmente
          (assinatura em papel).
          <br />
          Não é possível iniciar o processo de assinatura digital.
        </StatusMessage>
      );
    }

    if (processoIniciado) {
      return (
        <>
          <StatusMessage info>
            <FaExclamationTriangle size={24} /> O processo de assinatura já foi
            iniciado e está aguardando a assinatura do paciente.
          </StatusMessage>
          <br />
          <p className="pl-2">
            ID de Integração: <b>{contrato.ConExternalId}</b>
          </p>
        </>
      );
    }

    return (
      <>
        <WarningBox>
          <FaExclamationTriangle size={24} />
          <div>
            <strong>Atenção:</strong> Ao iniciar o processo de assinatura, os
            dados do contato não poderão ser alterados.
          </div>
        </WarningBox>

        <PatientInfo>
          <PatientRow>
            <PatientLabel>Nome:</PatientLabel>
            <PatientValue>{getNomeExibicao()}</PatientValue>
          </PatientRow>
          <PatientRow>
            <PatientLabel>
              {paciente.PacPessoaFisica ? 'CPF:' : 'CNPJ:'}
            </PatientLabel>
            <PatientValue>{getDocumentoFormatado()}</PatientValue>
          </PatientRow>
          <PatientRow>
            <PatientLabel>E-mail:</PatientLabel>
            <PatientValue>{paciente.PacEmail || 'Não informado'}</PatientValue>
          </PatientRow>
        </PatientInfo>

        <ActionButtons>
          <div style={{ flex: '1' }}>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleAssinarManual}
              disabled={loading}
            >
              Assinatura Física Impressa
            </Button>
          </div>
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="ml-2"
              onClick={handleIniciarAssinatura}
              disabled={loading}
            >
              {loading ? 'Iniciando...' : 'Iniciar Assinatura Digital'}
            </Button>
          </div>
        </ActionButtons>
      </>
    );
  };

  return (
    <Modal
      show={show}
      handleFecharModal={onClose}
      titulo="Assinatura Digital do Contrato"
      large
    >
      <Container>{renderContent()}</Container>
    </Modal>
  );
};

export default ModalAssinaturaContrato;
